/* "You may also like" row for the slide-out cart — see snippets/cart-recommendations-config.liquid.
 *
 * The slide-out cart on this store is drawn by the Llama Cart app (#lcu-llama-cart), not by the theme.
 * This script waits until that drawer is open, then asks Shopify's Product Recommendations API for
 * products related to what is in the cart and inserts a small horizontal row under the cart items.
 * Nothing is fetched until the drawer opens, so the drawer itself opens exactly as fast as before.
 * If the drawer markup is not found, or no recommendations come back, nothing is shown.
 */
(function () {
    // Cart lines asked for recommendations, newest first. Asking stops once the row is full, so the
    // extra lines are only fetched when the newest ones (e.g. a subscription box) have no recommendations.
    const MAX_SEEDS = 4;
    const FETCH_LIMIT = 10;
    // Rendered by sections/cart-recommendations-data.liquid, so each product has passed the brand
    // "Cart Price" (MSRP only) check that /recommendations/products.json cannot do.
    const DATA_SECTION = 'cart-recommendations-data';

    function escapeHtml (value) {
        return String(value == null ? '' : value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    // Paid cart lines, newest first, one per product. Free gifts and gift cards are not used as seeds.
    function pickSeeds (cart, max) {
        const seeds = [];
        ((cart && cart.items) || []).forEach((item) => {
            if (seeds.length >= max) return;
            if (item.gift_card || !(item.final_price > 0)) return;
            if (seeds.indexOf(item.product_id) === -1) seeds.push(item.product_id);
        });
        return seeds;
    }

    // Gift-app products (snippets/free-gift-product.liquid). Some are paid loyalty rewards, so price alone misses them.
    const GIFT_TAGS = ['_free_gift', '_free_gift_sample'];

    // Drops products already in the cart, sold out, free, gift-app products, or repeated; keeps API order.
    function filterRecs (products, cart, limit) {
        const inCart = ((cart && cart.items) || []).map((item) => item.product_id);
        const seen = [];
        return (products || []).filter((product) => {
            if (!product || !product.available || !(product.price > 0)) return false;
            if ((product.tags || []).some((tag) => GIFT_TAGS.indexOf(tag) !== -1)) return false;
            if (inCart.indexOf(product.id) !== -1 || seen.indexOf(product.id) !== -1) return false;
            seen.push(product.id);
            return true;
        }).slice(0, limit);
    }

    // Variant to add in one tap, or null when the shopper has to pick a shade/size first.
    function quickAddVariantId (product) {
        const variants = (product && product.variants) || [];
        if (variants.length !== 1 || !variants[0].available) return null;
        return variants[0].id;
    }

    function sizedImageUrl (src, width) {
        if (!src) return '';
        const url = src.indexOf('//') === 0 ? 'https:' + src : src;
        if (/[?&]width=/.test(url)) return url;
        return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
    }

    // Products from the data section's response; [] when it is missing or malformed.
    function parseRecsSection (html) {
        const match = /<script[^>]*data-cart-recs-products[^>]*>([\s\S]*?)<\/script>/.exec(html || '');
        if (!match) return [];
        try {
            const products = JSON.parse(match[1]);
            return Array.isArray(products) ? products : [];
        } catch (e) {
            return [];
        }
    }

    function formatMoney (cents) {
        const shopify = typeof window !== 'undefined' && window.Shopify;
        return shopify && typeof shopify.formatMoney === 'function'
            ? shopify.formatMoney(cents, window.money_format)
            : '$' + (cents / 100).toFixed(2);
    }

    function cardHtml (product, strings) {
        const variantId = quickAddVariantId(product);
        const title = escapeHtml(product.title);
        const url = escapeHtml(product.url || '/products/' + product.handle);
        const image = product.featured_image
            ? '<img src="' + escapeHtml(sizedImageUrl(product.featured_image, 240)) + '" alt="" width="120" height="120" loading="lazy">'
            : '';
        let price = '<span class="cart-recs__price">' + (product.price_varies ? escapeHtml(strings.from) + ' ' : '') + formatMoney(product.price) + '</span>';
        if (!product.price_varies && !product.msrp_only && product.compare_at_price > product.price) {
            price = '<s class="cart-recs__compare">' + formatMoney(product.compare_at_price) + '</s> ' + price;
        }
        const action = variantId
            ? '<button type="button" class="cart-recs__add" data-cart-recs-add="' + variantId + '" aria-label="' + escapeHtml(strings.add_aria.replace('[title]', product.title)) + '">' + escapeHtml(strings.add) + '</button>'
            : '<a class="cart-recs__add cart-recs__add--link" href="' + url + '" aria-label="' + escapeHtml(strings.choose_aria.replace('[title]', product.title)) + '">' + escapeHtml(strings.choose) + '</a>';
        return '<li class="cart-recs__item">' +
            '<a class="cart-recs__image" href="' + url + '" tabindex="-1" aria-hidden="true">' + image + '</a>' +
            '<span class="cart-recs__vendor">' + escapeHtml(product.vendor) + '</span>' +
            '<a class="cart-recs__title" href="' + url + '">' + title + '</a>' +
            '<span class="cart-recs__prices">' + price + '</span>' +
            action +
            '</li>';
    }

    // Section URL for the fallback collection, or null when no fallback is set.
    function fallbackUrl (root, handle, section) {
        const clean = String(handle || '').trim().replace(/^\/?collections\//, '').replace(/^\/+|\/+$/g, '');
        if (!clean) return null;
        return (root || '/') + 'collections/' + encodeURIComponent(clean) + '?section_id=' + section;
    }

    if (typeof module !== 'undefined' && module.exports) {
        module.exports = { pickSeeds, filterRecs, quickAddVariantId, sizedImageUrl, parseRecsSection, cardHtml, escapeHtml, fallbackUrl };
        return;
    }

    const configEl = document.getElementById('cart-recs-config');
    if (!configEl) return;
    let config;
    try {
        config = JSON.parse(configEl.textContent);
    } catch (e) {
        return;
    }
    const root = config.root || '/';
    const limit = Math.max(1, Math.min(6, parseInt(config.limit, 10) || 6));
    const recsCache = {};
    let drawer = null;
    let row = null;
    let latestCart = null;
    let renderedKey = '';
    let updating = false;
    let updateAgain = false;
    let cssLoaded = false;

    function findOpenDrawer () {
        return drawer && drawer.querySelector('.lcu__drawer--active');
    }

    function loadCss () {
        if (cssLoaded || !config.css) return;
        cssLoaded = true;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = config.css;
        document.head.appendChild(link);
    }

    function getJson (url) {
        return fetch(url, { headers: { Accept: 'application/json' } }).then((res) => {
            if (!res.ok) throw new Error(res.status);
            return res.json();
        });
    }

    function getSection (url) {
        return fetch(url).then((res) => {
            if (!res.ok) throw new Error(res.status);
            return res.text();
        }).then(parseRecsSection);
    }

    function getRecs (productId) {
        if (recsCache[productId]) return recsCache[productId];
        const url = (intent) => root + 'recommendations/products?section_id=' + DATA_SECTION + '&product_id=' + productId + '&limit=' + FETCH_LIMIT + '&intent=' + intent;
        recsCache[productId] = getSection(url(config.intent === 'complementary' ? 'complementary' : 'related'))
            .then((products) => {
                // Complementary picks only exist once they are set up in Search & Discovery; fall back to related.
                if (!products.length && config.intent === 'complementary') {
                    return getSection(url('related'));
                }
                return products;
            })
            .catch(() => {
                delete recsCache[productId];
                return [];
            });
        return recsCache[productId];
    }

    let fallbackRecs = null;
    // Products from the fallback collection, fetched once per page and only when needed.
    function getFallbackRecs () {
        const url = fallbackUrl(root, config.fallback, DATA_SECTION);
        if (!url) return Promise.resolve([]);
        if (!fallbackRecs) {
            fallbackRecs = getSection(url).catch(() => {
                fallbackRecs = null;
                return [];
            });
        }
        return fallbackRecs;
    }

    function hideRow () {
        renderedKey = '';
        if (row) row.hidden = true;
    }

    // Keeps the row directly under the list of cart lines, even after the app re-renders the drawer.
    function placeRow () {
        const open = findOpenDrawer();
        const anchor = open && open.querySelector('.lcu__drawer__cart-items');
        if (!row || !anchor) return false;
        if (anchor.nextElementSibling !== row) anchor.insertAdjacentElement('afterend', row);
        return true;
    }

    function buildRow () {
        row = document.createElement('section');
        row.className = 'cart-recs';
        row.hidden = true;
        row.setAttribute('aria-labelledby', 'cart-recs-title');
        row.innerHTML =
            '<div class="cart-recs__head">' +
                '<h3 class="cart-recs__heading" id="cart-recs-title">' + escapeHtml(config.title) + '</h3>' +
                '<div class="cart-recs__arrows">' +
                    '<button type="button" class="cart-recs__arrow" data-cart-recs-scroll="-1" aria-label="' + escapeHtml(config.strings.previous) + '">&#8249;</button>' +
                    '<button type="button" class="cart-recs__arrow" data-cart-recs-scroll="1" aria-label="' + escapeHtml(config.strings.next) + '">&#8250;</button>' +
                '</div>' +
            '</div>' +
            '<ul class="cart-recs__list" role="list"></ul>' +
            '<p class="cart-recs__status" role="status" aria-live="polite"></p>';
        row.addEventListener('click', onRowClick);
    }

    async function update () {
        if (updating) {
            updateAgain = true;
            return;
        }
        updating = true;
        try {
            if (!findOpenDrawer()) return;
            if (!latestCart) latestCart = await getJson(root + 'cart.js');
            const cart = latestCart;
            const seeds = pickSeeds(cart, MAX_SEEDS);
            if (!cart.item_count || !seeds.length) {
                hideRow();
                return;
            }
            const key = seeds.join(',') + '|' + cart.items.map((item) => item.product_id).join(',');
            if (key === renderedKey) {
                placeRow();
                return;
            }
            let products = [];
            for (const seed of seeds) {
                products = filterRecs(products.concat(await getRecs(seed)), cart, limit);
                if (products.length >= limit) break;
            }
            // Shopify has no recommendations for some brands (all of EltaMD): top up from the fallback collection.
            if (products.length < limit) {
                products = filterRecs(products.concat(await getFallbackRecs()), cart, limit);
            }
            if (cart !== latestCart) {
                updateAgain = true;
                return;
            }
            if (!products.length) {
                hideRow();
                return;
            }
            loadCss();
            if (!row) buildRow();
            row.querySelector('.cart-recs__list').innerHTML = products.map((p) => cardHtml(p, config.strings)).join('');
            row.querySelector('.cart-recs__status').textContent = '';
            row.classList.toggle('cart-recs--scrollable', products.length > 3);
            row.hidden = !placeRow();
            renderedKey = row.hidden ? '' : key;
        } catch (e) {
            hideRow();
        } finally {
            updating = false;
            if (updateAgain) {
                updateAgain = false;
                scheduleUpdate();
            }
        }
    }

    function scheduleUpdate () {
        // Wait until the browser is idle so the drawer's own render and slide-in come first.
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(update, { timeout: 800 });
        } else {
            setTimeout(update, 200);
        }
    }

    async function refreshCart () {
        if (window._lcu && window._lcu.utils && typeof window._lcu.utils.refreshShopifyCart === 'function') {
            return window._lcu.utils.refreshShopifyCart();
        }
        const cart = await getJson(root + 'cart.js');
        document.dispatchEvent(new CustomEvent('upsell:cart-updated', { detail: { newCart: cart } }));
        return cart;
    }

    async function onRowClick (event) {
        const arrow = event.target.closest('[data-cart-recs-scroll]');
        if (arrow) {
            const list = row.querySelector('.cart-recs__list');
            list.scrollBy({ left: list.clientWidth * 0.8 * Number(arrow.dataset.cartRecsScroll), behavior: 'smooth' });
            return;
        }
        const button = event.target.closest('[data-cart-recs-add]');
        if (!button || button.disabled) return;
        const status = row.querySelector('.cart-recs__status');
        button.disabled = true;
        button.textContent = config.strings.adding;
        try {
            const res = await fetch(root + 'cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ items: [{ id: Number(button.dataset.cartRecsAdd), quantity: 1 }] })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.description || config.strings.error);
            button.textContent = config.strings.added;
            status.classList.remove('cart-recs__status--error');
            status.textContent = config.strings.added;
            await refreshCart();
        } catch (e) {
            button.disabled = false;
            button.textContent = config.strings.add;
            status.classList.add('cart-recs__status--error');
            status.textContent = e.message || config.strings.error;
        }
    }

    function watchDrawer () {
        let wasOpen = false;
        let queued = false;
        new MutationObserver(() => {
            if (queued) return;
            queued = true;
            window.requestAnimationFrame(() => {
                queued = false;
                const open = !!findOpenDrawer();
                if (open && !wasOpen) scheduleUpdate();
                else if (open && renderedKey) placeRow();
                wasOpen = open;
            });
        }).observe(drawer, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
    }

    document.addEventListener('lcu:smart-cart:updated', (event) => {
        if (event.detail && event.detail.newCart) latestCart = event.detail.newCart;
        if (findOpenDrawer()) scheduleUpdate();
    });

    // The app renders its drawer after page load; look for it for up to 20 seconds.
    let tries = 0;
    (function findDrawer () {
        drawer = document.getElementById('lcu-llama-cart');
        if (drawer) {
            watchDrawer();
            if (findOpenDrawer()) scheduleUpdate();
        } else if (++tries < 40) {
            setTimeout(findDrawer, 500);
        }
    })();
})();
